import { ReactNode, useId, useState } from 'react';
import { Caption1, Subtitle2, mergeClasses } from '@fluentui/react-components';
import { ChevronDownRegular, ChevronRightRegular } from '@fluentui/react-icons';

import { useChangeV2Styles } from './styles';

type TProps = {
  title: string;
  description?: string;
  /** Optional right-aligned header content, e.g. a badge or a small action. */
  headerAction?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Collapsible sections start closed and keep their fields mounted. */
  collapsible?: boolean;
};

// A titled surface card. Used for every form section and side-panel block so the
// page reads as one system with clear hierarchy.
export const SectionCard = ({ title, description, headerAction, children, className, collapsible }: TProps) => {
  const classes = useChangeV2Styles();
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const heading = (
    <>
      <Subtitle2>{title}</Subtitle2>
      {description ? <Caption1 className={classes.muted}>{description}</Caption1> : null}
    </>
  );

  return (
    <section className={mergeClasses(classes.surface, className)}>
      <div className="flex items-start justify-between gap-4">
        {collapsible ? (
          <button
            type="button"
            className="flex flex-1 items-start justify-between gap-4 text-left"
            aria-expanded={expanded}
            aria-controls={contentId}
            aria-label={title}
            onClick={() => setExpanded((value) => !value)}
          >
            <span className={classes.sectionHeader}>{heading}</span>
            {expanded ? <ChevronDownRegular className="shrink-0" /> : <ChevronRightRegular className="shrink-0" />}
          </button>
        ) : (
          <div className={classes.sectionHeader}>{heading}</div>
        )}
        {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
      </div>
      <div id={contentId} hidden={collapsible && !expanded}>
        {children}
      </div>
    </section>
  );
};
